/**
 * Message contract between the Chrome extension and this app.
 *
 * The extension's content script runs in the app's origin and re-posts the crop
 * with `window.postMessage`, so the app only ever trusts same-window,
 * same-origin messages carrying this exact envelope.
 *
 * Clipboard paste remains the primary path - it needs no extension at all - and
 * this bridge exists so "Send to Design AI" can skip the paste step and carry
 * provenance (which listing the crop came from) along with the pixels.
 */
export const BRIDGE_SOURCE = 'design-ai-extension';
export const BRIDGE_VERSION = 1;

export interface BridgeCropMessage {
  source: typeof BRIDGE_SOURCE;
  version: typeof BRIDGE_VERSION;
  type: 'crop';
  payload: {
    /** PNG data URL of the cropped region at its native resolution. */
    dataUrl: string;
    width: number;
    height: number;
    /** Page the crop was taken from, for the provenance label. */
    pageUrl?: string;
    /** Original image URL, when the extension could resolve one. */
    imageUrl?: string;
  };
}

export function isBridgeCropMessage(value: unknown): value is BridgeCropMessage {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Partial<BridgeCropMessage>;
  return (
    message.source === BRIDGE_SOURCE &&
    message.version === BRIDGE_VERSION &&
    message.type === 'crop' &&
    typeof message.payload?.dataUrl === 'string'
  );
}
